import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { refreshQuota } from "./QuotaBadge";
import { UploadRemainingBanner } from "./UploadRemainingBanner";
import { INGREDIENT_CATEGORIES, euro } from "../lib/constants";
import { compressImageIfNeeded } from "../lib/imageCompress";
import { toast } from "sonner";
import {
    Upload,
    FileScan,
    Loader2,
    X,
    Trash2,
    AlertTriangle,
    CheckCircle2,
    Sparkles,
    Camera,
} from "lucide-react";

const UNITS = ["kg", "g", "l", "ml", "pz"];

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Dialog 3-step:
 *  1. upload  → utente seleziona PDF/JPG/PNG
 *  2. extract → spinner con Gemini Vision
 *  3. review  → tabella editabile, l'utente conferma → import
 */
export function OcrPurchaseDialog({ open, onOpenChange, onImported }) {
    const [step, setStep] = useState("upload"); // upload | extracting | review
    const [file, setFile] = useState(null);
    const [supplier, setSupplier] = useState("");
    const [docDate, setDocDate] = useState(todayIso());
    const [rows, setRows] = useState([]);
    const [importing, setImporting] = useState(false);
    const [compressing, setCompressing] = useState(false);
    const inputRef = useRef(null);
    const cameraRef = useRef(null);

    const reset = () => {
        setStep("upload");
        setFile(null);
        setSupplier("");
        setDocDate(todayIso());
        setRows([]);
        setImporting(false);
        setCompressing(false);
    };

    useEffect(() => {
        if (!open) reset();
    }, [open]);

    const onPick = async (f) => {
        if (!f) return;
        const okType =
            f.type === "application/pdf" ||
            f.type === "image/jpeg" ||
            f.type === "image/png" ||
            f.type === "image/webp" ||
            f.type === "image/heic" ||
            f.type === "image/heif" ||
            /\.(pdf|jpe?g|png|webp|heic|heif)$/i.test(f.name);
        if (!okType) {
            toast.error("Formato non supportato. Usa PDF, JPG, PNG, WEBP o foto del cellulare.");
            return;
        }
        if (f.size > 25 * 1024 * 1024) {
            toast.error("File troppo grande (max 25 MB). Comprimilo o riduci la risoluzione.");
            return;
        }
        // Comprime automaticamente immagini > 3 MB lato client (più veloce upload + AI)
        if (f.type.startsWith("image/") && f.size > 3 * 1024 * 1024) {
            setCompressing(true);
            try {
                const compressed = await compressImageIfNeeded(f);
                if (compressed.size < f.size) {
                    const savedKb = Math.round((f.size - compressed.size) / 1024);
                    toast.success(`Foto ottimizzata (-${savedKb} KB) per upload più veloce`);
                }
                setFile(compressed);
            } catch {
                setFile(f);
            } finally {
                setCompressing(false);
            }
        } else {
            setFile(f);
        }
    };

    const startExtract = async () => {
        if (!file) return;
        setStep("extracting");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/purchases/ocr-extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 110000,
            });
            setSupplier(data.supplier || "");
            if (data.document_date) setDocDate(data.document_date);
            setRows(
                (data.items || []).map((it, i) => ({
                    _id: `r-${i}`,
                    name: it.name,
                    quantity: it.quantity,
                    unit: it.unit,
                    total_price: it.total_price,
                    category: it.category || "dispensa",
                    matched_ingredient_id: it.matched_ingredient_id || null,
                    matched_ingredient_name: it.matched_ingredient_name || null,
                }))
            );
            setStep("review");
        } catch (e) {
            const code = e?.code;
            if (code === "ECONNABORTED" || code === "ERR_NETWORK" || /timeout/i.test(e?.message || "")) {
                toast.error("L'analisi OCR è troppo lunga. Prova con un file più piccolo (≤ 2 MB) o fotografando solo le pagine rilevanti della fattura.");
            } else {
                toast.error(formatApiError(e));
            }
            setStep("upload");
        } finally {
            refreshQuota();
        }
    };

    const updateRow = (id, patch) =>
        setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));
    const removeRow = (id) => setRows((prev) => prev.filter((r) => r._id !== id));

    const importNow = async () => {
        if (rows.length === 0) {
            toast.error("Nessuna riga da importare");
            return;
        }
        // Validazione: ogni riga senza match deve avere una categoria
        for (const r of rows) {
            if (!r.matched_ingredient_id && !r.category) {
                toast.error(`Seleziona una categoria per "${r.name}"`);
                return;
            }
        }
        setImporting(true);
        try {
            const payload = {
                supplier,
                date: docDate,
                rows: rows.map((r) => ({
                    name: r.name,
                    quantity: parseFloat(r.quantity),
                    unit: r.unit,
                    total_price: parseFloat(r.total_price),
                    category: r.category,
                    matched_ingredient_id: r.matched_ingredient_id || null,
                })),
            };
            const { data } = await api.post("/purchases/ocr-import", payload);
            const parts = [];
            if (data.imported) parts.push(`${data.imported} acquisti importati`);
            if (data.ingredients_created)
                parts.push(`${data.ingredients_created} nuovi ingredienti`);
            toast.success(parts.join(" · ") || "Import completato");
            onImported?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setImporting(false);
        }
    };

    const totalPrice = rows.reduce(
        (s, r) => s + (parseFloat(r.total_price) || 0),
        0
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="ocr-dialog"
                className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0 bg-brand-cream"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-brand-line bg-paper">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                            <Sparkles size={20} />
                        </div>
                        <div className="flex-1">
                            <div className="eyebrow text-brand-gold mb-1">Importazione AI</div>
                            <DialogTitle className="font-display text-2xl text-brand-ink">
                                Carica DDT o fattura
                            </DialogTitle>
                            <DialogDescription className="text-sm text-brand-muted mt-1">
                                Riconosciamo automaticamente prodotti, quantità e costi. Tu controlli e confermi.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                {step === "upload" && (
                    <div className="px-6 py-8 flex-1 overflow-y-auto" data-testid="ocr-upload-step">
                        <div className="mb-5">
                            <UploadRemainingBanner kind="invoices" label="Fatture/DDT" />
                        </div>
                        <label
                            htmlFor="ocr-file"
                            className={`block border-2 border-dashed rounded-2xl p-8 sm:p-10 text-center cursor-pointer transition-colors ${
                                file
                                    ? "border-brand-gold bg-brand-gold/5"
                                    : "border-brand-line bg-paper hover:border-brand-gold/60 hover:bg-brand-gold/5"
                            }`}
                        >
                            <input
                                ref={inputRef}
                                id="ocr-file"
                                data-testid="ocr-file-input"
                                type="file"
                                className="hidden"
                                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                                onChange={(e) => onPick(e.target.files?.[0])}
                            />
                            {/* Input separato con capture=environment per aprire direttamente
                                la fotocamera posteriore del cellulare */}
                            <input
                                ref={cameraRef}
                                id="ocr-camera"
                                data-testid="ocr-camera-input"
                                type="file"
                                className="hidden"
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => onPick(e.target.files?.[0])}
                            />
                            {compressing ? (
                                <div className="space-y-2">
                                    <Loader2 className="mx-auto text-brand-gold animate-spin" size={36} />
                                    <div className="font-display text-xl text-brand-ink">Ottimizzazione foto…</div>
                                    <div className="text-xs text-brand-muted">Riduco le dimensioni per un upload più veloce</div>
                                </div>
                            ) : file ? (
                                <div className="space-y-2">
                                    <FileScan className="mx-auto text-brand-gold" size={36} />
                                    <div className="font-display text-xl text-brand-ink break-all">{file.name}</div>
                                    <div className="text-xs text-brand-muted">
                                        {file.size > 1024 * 1024
                                            ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                            : `${(file.size / 1024).toFixed(0)} KB`}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Upload className="mx-auto text-brand-muted" size={36} strokeWidth={1.4} />
                                    <div className="font-display text-xl text-brand-ink">
                                        Trascina qui il documento
                                    </div>
                                    <div className="text-xs text-brand-muted">
                                        oppure usa i pulsanti qui sotto · PDF, JPG, PNG, foto · max 25 MB
                                    </div>
                                </div>
                            )}
                        </label>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                disabled={compressing}
                                data-testid="ocr-pick-file-btn"
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-line bg-paper text-sm font-semibold text-brand-ink hover:border-brand-gold/60 hover:bg-brand-gold/5 transition disabled:opacity-50"
                            >
                                <Upload size={14} /> Scegli file
                            </button>
                            <button
                                type="button"
                                onClick={() => cameraRef.current?.click()}
                                disabled={compressing}
                                data-testid="ocr-pick-camera-btn"
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-gold/60 bg-brand-gold/10 text-sm font-bold text-brand-navy hover:bg-brand-gold/20 transition disabled:opacity-50"
                            >
                                <Camera size={14} /> Scatta foto
                            </button>
                        </div>

                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                className="px-5 py-2.5 rounded-full border border-brand-line text-sm font-semibold text-brand-ink hover:bg-paper transition"
                                data-testid="ocr-cancel"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={startExtract}
                                disabled={!file || compressing}
                                data-testid="ocr-extract-btn"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navyLight disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                <Sparkles size={14} /> Analizza con AI
                            </button>
                        </div>
                    </div>
                )}

                {step === "extracting" && (
                    <div
                        className="px-6 py-16 flex flex-col items-center justify-center text-center flex-1"
                        data-testid="ocr-extracting"
                    >
                        <Loader2 size={36} className="animate-spin text-brand-gold mb-4" />
                        <div className="font-display text-2xl text-brand-ink">
                            Analisi in corso…
                        </div>
                        <div className="text-sm text-brand-muted mt-2 max-w-md">
                            Stiamo leggendo il tuo documento. Possono volerci 10-30 secondi
                            per riconoscere prodotti, quantità e prezzi.
                        </div>
                    </div>
                )}

                {step === "review" && (
                    <>
                        <div className="px-6 py-4 border-b border-brand-line bg-paper grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-muted mb-1">
                                    Fornitore
                                </label>
                                <input
                                    data-testid="ocr-supplier"
                                    value={supplier}
                                    onChange={(e) => setSupplier(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-muted mb-1">
                                    Data documento
                                </label>
                                <input
                                    data-testid="ocr-date"
                                    type="date"
                                    value={docDate}
                                    onChange={(e) => setDocDate(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4" data-testid="ocr-review-step">
                            {rows.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-brand-line bg-paper p-10 text-center text-sm text-brand-muted">
                                    Nessun prodotto da importare.
                                </div>
                            ) : (
                                <div className="space-y-2.5">
                                    {rows.map((r) => (
                                        <ReviewRow
                                            key={r._id}
                                            row={r}
                                            onChange={(patch) => updateRow(r._id, patch)}
                                            onRemove={() => removeRow(r._id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-brand-line bg-paper flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-sm text-brand-muted">
                                <span className="font-semibold text-brand-ink">{rows.length}</span> righe ·{" "}
                                <span className="font-semibold text-brand-ink">{euro(totalPrice)}</span>
                            </div>
                            <div className="flex gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => setStep("upload")}
                                    className="px-4 py-2.5 rounded-full border border-brand-line text-sm font-semibold text-brand-ink hover:bg-brand-cream transition"
                                    data-testid="ocr-back"
                                >
                                    Indietro
                                </button>
                                <button
                                    type="button"
                                    onClick={importNow}
                                    disabled={importing || rows.length === 0}
                                    data-testid="ocr-confirm-btn"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-gold text-brand-navy text-sm font-semibold hover:bg-brand-goldDark disabled:opacity-50 transition"
                                >
                                    {importing ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <CheckCircle2 size={14} />
                                    )}
                                    {importing ? "Importo…" : "Conferma e importa"}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ReviewRow({ row, onChange, onRemove }) {
    const isMatched = !!row.matched_ingredient_id;
    return (
        <div
            className={`bg-paper rounded-xl border p-3.5 grid grid-cols-12 gap-2 items-center ${
                isMatched ? "border-emerald-200" : "border-amber-200"
            }`}
            data-testid={`ocr-row-${row._id}`}
        >
            <div className="col-span-12 sm:col-span-4">
                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                    Prodotto
                </label>
                <input
                    value={row.name}
                    onChange={(e) => onChange({ name: e.target.value })}
                    className="w-full px-2.5 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                />
                {isMatched ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700">
                        <CheckCircle2 size={11} /> abbinato a "{row.matched_ingredient_name}"
                    </div>
                ) : (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700">
                        <AlertTriangle size={11} /> nuovo ingrediente — scegli categoria
                    </div>
                )}
            </div>

            <div className="col-span-4 sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                    Quantità
                </label>
                <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={row.quantity}
                    onChange={(e) => onChange({ quantity: e.target.value })}
                    className="w-full px-2.5 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                />
            </div>

            <div className="col-span-4 sm:col-span-1">
                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                    UM
                </label>
                <select
                    value={row.unit}
                    onChange={(e) => onChange({ unit: e.target.value })}
                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                >
                    {UNITS.map((u) => (
                        <option key={u} value={u}>
                            {u}
                        </option>
                    ))}
                </select>
            </div>

            <div className="col-span-4 sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                    Totale €
                </label>
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={row.total_price}
                    onChange={(e) => onChange({ total_price: e.target.value })}
                    className="w-full px-2.5 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                />
            </div>

            <div className="col-span-10 sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                    Categoria
                </label>
                <select
                    value={row.category}
                    disabled={isMatched}
                    onChange={(e) => onChange({ category: e.target.value })}
                    className={`w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold ${
                        isMatched ? "opacity-60" : ""
                    }`}
                >
                    {INGREDIENT_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </select>
            </div>

            <div className="col-span-2 sm:col-span-1 flex justify-end">
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Rimuovi riga"
                    className="h-9 w-9 rounded-full text-brand-muted hover:bg-red-50 hover:text-red-600 transition flex items-center justify-center"
                >
                    <Trash2 size={15} />
                </button>
            </div>
        </div>
    );
}
